// Pure track-stats + derived-point helpers for the Track Details / Edit redesign.
//
// A track's geometry is stored as GeoJSON: features[0].geometry.coordinates is an
// array of [lng, lat, elevation(m)] triples, and features[0].properties.coordTimes is
// an index-aligned array of [epochSeconds] timestamps. Trimming slices both arrays to
// the kept range; stats are recomputed from the slice.
//
// This module is dependency-free (no google.maps) so it is unit testable. Distance
// uses the haversine formula with the same earth radius google.maps.geometry.spherical
// uses. All outputs are SI base units (meters, m/s, seconds); the UI formats to the
// user's units (mi/ft/mph or km/m/kph).

const EARTH_RADIUS_M = 6378137 // matches google.maps spherical computeDistanceBetween
// Segments slower than this are treated as "stopped" (matches the design's 0.5 mph).
export const STOPPED_THRESHOLD_MPS = 0.2235 // 0.5 mph

const toRad = deg => (deg * Math.PI) / 180

// Distance in meters between two [lng, lat, ...] coordinates.
export const segmentDistanceMeters = (a, b) => {
  const lat1 = toRad(a[1])
  const lat2 = toRad(b[1])
  const dLat = lat2 - lat1
  const dLng = toRad(b[0] - a[0])
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

// Read a timestamp from a coordTimes entry and normalize to epoch SECONDS.
// coordTimes arrive in either seconds (~1.78e9) or milliseconds (~1.78e12) depending
// on the source; anything above ~1e11 is milliseconds and is divided by 1000.
const timeAt = (coordTimes, i) => {
  const v = Array.isArray(coordTimes[i]) ? coordTimes[i][0] : coordTimes[i]
  return typeof v === 'number' && v > 1e11 ? v / 1000 : v
}

const hasAlignedTimes = (coordinates, coordTimes) =>
  Array.isArray(coordTimes) && coordTimes.length === coordinates.length

/**
 * Build an index-aligned array of derived per-point values for the charts.
 * Each entry: { distM (cumulative), elevM, mps, timeS, gradePct }.
 * Speed/time are 0/undefined when coordTimes are absent.
 */
export function buildTrackPoints(coordinates = [], coordTimes = null) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return []
  const times = hasAlignedTimes(coordinates, coordTimes) ? coordTimes : null
  const out = []
  let cum = 0
  for (let i = 0; i < coordinates.length; i++) {
    const elevM = coordinates[i][2]
    let segDist = 0
    let mps = 0
    let gradePct = 0
    if (i > 0) {
      segDist = segmentDistanceMeters(coordinates[i - 1], coordinates[i])
      cum += segDist
      const dElev = elevM - coordinates[i - 1][2]
      gradePct = segDist > 0 ? (dElev / segDist) * 100 : 0
      if (times) {
        const dt = timeAt(times, i) - timeAt(times, i - 1)
        mps = dt > 0 ? segDist / dt : 0
      }
    }
    out.push({
      distM: cum,
      elevM,
      mps,
      timeS: times ? timeAt(times, i) : undefined,
      gradePct
    })
  }
  // Point 0 inherits point 1's speed/grade so the head of the chart isn't flat-zero.
  if (out.length > 1) {
    out[0].mps = out[1].mps
    out[0].gradePct = out[1].gradePct
  }
  return out
}

/**
 * Recompute track stats from a (possibly trimmed) coordinate slice.
 * Returns SI base units merged over originalStats (which may carry mobile-provided
 * fields we don't recompute).
 */
export function computeTrackStats(coordinates, coordTimes, originalStats = {}) {
  const stats = { ...originalStats }
  if (!Array.isArray(coordinates) || coordinates.length < 2) return stats

  let distanceM = 0
  let elevationGainM = 0
  let minAltM = coordinates[0][2]
  let maxAltM = coordinates[0][2]

  for (let i = 1; i < coordinates.length; i++) {
    distanceM += segmentDistanceMeters(coordinates[i - 1], coordinates[i])
    const prev = coordinates[i - 1][2]
    const ele = coordinates[i][2]
    if (typeof ele === 'number') {
      if (typeof prev === 'number' && ele > prev) elevationGainM += ele - prev
      if (ele < minAltM) minAltM = ele
      if (ele > maxAltM) maxAltM = ele
    }
  }

  stats.distanceM = distanceM
  stats.elevationGainM = elevationGainM
  stats.minAltM = minAltM
  stats.maxAltM = maxAltM

  if (hasAlignedTimes(coordinates, coordTimes)) {
    const startTimeS = timeAt(coordTimes, 0)
    const stopTimeS = timeAt(coordTimes, coordinates.length - 1)
    const realTripTimeS = Math.max(0, stopTimeS - startTimeS)

    let maxSpeedMps = 0
    let movingTimeS = 0
    // Track contiguous stopped runs for stop count + longest stop.
    let stopCount = 0
    let longestStopS = 0
    let curStopS = 0
    let inStop = false

    for (let i = 1; i < coordinates.length; i++) {
      const segDist = segmentDistanceMeters(coordinates[i - 1], coordinates[i])
      const segDt = timeAt(coordTimes, i) - timeAt(coordTimes, i - 1)
      if (segDt > 0) {
        const speed = segDist / segDt
        if (speed > maxSpeedMps) maxSpeedMps = speed
        if (speed >= STOPPED_THRESHOLD_MPS) {
          movingTimeS += segDt
          if (inStop) { longestStopS = Math.max(longestStopS, curStopS); inStop = false; curStopS = 0 }
        } else {
          if (!inStop) { inStop = true; stopCount += 1; curStopS = 0 }
          curStopS += segDt
        }
      }
    }
    if (inStop) longestStopS = Math.max(longestStopS, curStopS)

    const stoppedTimeS = Math.max(0, realTripTimeS - movingTimeS)
    stats.startTimeS = startTimeS
    stats.stopTimeS = stopTimeS
    stats.realTripTimeS = realTripTimeS
    stats.movingTimeS = movingTimeS
    stats.stoppedTimeS = stoppedTimeS
    stats.stopCount = stopCount
    stats.longestStopS = longestStopS
    stats.maxSpeedMps = maxSpeedMps
    stats.avgSpeedMps = realTripTimeS > 0 ? distanceM / realTripTimeS : 0
    stats.movingAvgMps = movingTimeS > 0 ? distanceM / movingTimeS : 0
  }

  return stats
}

// ---- Unit + format helpers (imperial/metric aware) ----
export const M_PER_MI = 1609.344
export const FT_PER_M = 3.28084
export const MPS_TO_MPH = 2.2369362920544
export const MPS_TO_KPH = 3.6

export const metersToDisplayDistance = (m, units) =>
  units === 'imperial' ? m / M_PER_MI : m / 1000 // mi or km
export const metersToElev = (m, units) =>
  units === 'imperial' ? m * FT_PER_M : m // ft or m
export const mpsToSpeed = (mps, units) =>
  units === 'imperial' ? mps * MPS_TO_MPH : mps * MPS_TO_KPH

export const distanceUnit = units => (units === 'imperial' ? 'mi' : 'km')
export const elevUnit = units => (units === 'imperial' ? 'ft' : 'm')
export const speedUnit = units => (units === 'imperial' ? 'mph' : 'kph')

// Seconds -> "H:MM:SS" or "M:SS".
export const formatDuration = (totalSeconds) => {
  const s = Math.max(0, Math.round(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0')
  const ss = String(sec).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}
